import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook to track scroll position and provide scroll-based functionality
 * @param {number} threshold - Scroll threshold to trigger scrolled state
 * @returns {Object} Scroll state and utilities
 */
export const useScrollPosition = (threshold = 50) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [scrollDirection, setScrollDirection] = useState('down');
  const [lastScrollY, setLastScrollY] = useState(0);

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    
    // Update scroll position
    setScrollY(currentScrollY);
    setIsScrolled(currentScrollY > threshold);
    
    // Determine scroll direction
    if (currentScrollY > lastScrollY && currentScrollY > 100) {
      setScrollDirection('down');
    } else {
      setScrollDirection('up');
    }
    
    setLastScrollY(currentScrollY);
  }, [threshold, lastScrollY]);

  useEffect(() => {
    // Sync once on mount (mobile Safari can restore a non-zero scroll)
    handleScroll();

    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [handleScroll]);

  return {
    isScrolled,
    scrollY,
    scrollDirection,
    scrollToTop: () => window.scrollTo({ top: 0, behavior: 'smooth' }),
    scrollToElement: (elementId) => {
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };
};
